import { flatten } from '../../util/array';
import geometry from '../../scene/util/geometry';
import tacHelpers from '../../state/tac/tacHelpers';
import productService from './';
import { ITEMS } from '../../constants';

let products;
let productMap;
let modelMap;

const connectionCache = {
  tac: null,
  item: null,
};

export function init(files) {
  products = files.find(file => file.hasOwnProperty('products')).products;
  productMap = products.reduce((map, prod) => {
    map[prod.id] = prod;
    return map;
  }, {});

  modelMap = files.find(file => file.hasOwnProperty('models')).models;
}

function getModel(item) {
  const product = productMap[item.id];
  return modelMap[product.modelid];
}

export function getBounds(model, type) {
  if (!model.bounds) {
    // this is not a model
    model = getModel(model);
  }
  // we want the largest bound so we sort them by volume
  const boundsBySize = Object.values(model.bounds)
    .filter(bounds => bounds.type === type)
    .sort(
      (a, b) =>
        a.size.height * a.size.width * a.size.depth -
        b.size.height * b.size.width * b.size.depth
    );
  if (boundsBySize.length) {
    return boundsBySize.pop();
  }

  // if there are no space bounds, try collision bounds,
  // since default bounds might be 'size'
  if (type === 'space') {
    return getBounds(model, 'collision');
  }

  // there were no bounds of specified type
  return model.bounds.default;
}

function moveOrigo(connection, model) {
  const bounds = model.bounds.default.size;
  const out = {
    x: connection.x + bounds.width / 2,
    y: connection.y + bounds.height / 2,
    z: connection.z + bounds.depth / 2,
    rx: connection.rx,
    ry: connection.ry,
    rz: connection.rz,
    name: connection.name,
  };

  return out;
}

export function getProppingBounds(item) {
  const model = getModel(item);
  return Object.values(model.bounds).filter(
    bounds => bounds.type === 'propping'
  );
}

export function getConnections(item) {
  const model = getModel(item);
  return Object.entries(model.connections).reduce((out, [key, val]) => {
    out[key] = moveOrigo(val, model);
    out[key][key] = key;
    return out;
  }, {});
}

function getOffsets(item, connectionName) {
  const connections = getConnections(item);
  const localConn =
    connectionName && connections[connectionName]
      ? [connections[connectionName]]
      : Object.values(connections);
  if (localConn.length === 1) {
    return localConn[0];
  }
  return { x: 0, y: 0, z: 0 };
}

function getOffset(item, connectionName, prop) {
  const offsets = getOffsets(item, connectionName);
  return -offsets[prop] || 0;
}

export function getYoffset(item, connectionName) {
  return getOffset(item, connectionName, 'y');
}

function matchingConnections(model, parent) {
  const parentModel = getModel(parent);

  const modelConnections = Object.keys(model.connections);
  const parentsRelations = flatten(Object.values(parentModel.relations));
  return parentsRelations
    .filter(function (relation) {
      return modelConnections.includes(relation.child);
    })
    .map(function (relation) {
      const conn = parentModel.connections[relation.parent];
      conn.rx = relation.pos.rx;
      conn.ry = relation.pos.ry;
      conn.rz = relation.pos.rz;
      conn.name = relation.child;
      return conn;
    });
}

export function isWallMounted(model) {
  if (!model.bounds) {
    // this is not a model
    model = getModel(model);
  }
  return !!model.logic.wall;
}

export function isFloorStanding(model) {
  if (!model.bounds) {
    // this is not a model
    model = getModel(model);
  }
  return !!model.logic.floor;
}

export function getMatchingConnections(item, tac) {
  if (!item || !tac) {
    throw new Error('getConnections: bad input');
  }

  const product = productMap[item.id];
  if (!product) {
    throw new Error('getConnections: bad product');
  }

  const model = modelMap[product.modelid];
  if (!model) {
    throw new Error('getConnections: bad model');
  }
  if (item === connectionCache.item && tac === connectionCache.tac) {
    return connectionCache.connections;
  }

  // now that we're sure that the item has non-generic connections,
  // map all the connections to matching parents
  // ignoring the groups
  const out = tacHelpers
    .getAllItems(tac.items, item)
    .map(parent => {
      const out = Object.assign({}, parent);
      const parentModel = getModel(parent);
      out.connections = matchingConnections(model, parent).map(connection =>
        moveOrigo(connection, parentModel)
      );
      return out;
    })
    .filter(item => item.connections.length);
  if (isWallMounted(model)) {
    out.push({
      wall: true,
      connections: [],
    });
  }

  if (isFloorStanding(model)) {
    out.push({
      floor: true,
      connections: [],
    });
  }

  const withVariant = out.map(group => ({
    ...group,
    variant: item,
  }));

  connectionCache.tac = tac;
  connectionCache.item = item;
  connectionCache.connections = withVariant;

  return withVariant;
}

function getOnlyMatchingConnectionPoint(child, parent) {
  const connParents = getMatchingConnections(child, { items: [parent] });
  return (
    connParents?.[0]?.connections?.length === 1 && connParents[0].connections[0]
  );
}

export function fitToPoint(item, point, boundsType, skipChildren = false) {
  const bounds = getBounds(getModel(item), boundsType);

  let size;
  let pos;

  if (!skipChildren && item.items && item.items.length) {
    const parent = Object.assign({}, bounds.pos, bounds.size);
    const merged = geometry.mergeKids(
      parent,
      item.items.map(child => {
        const fitted = productService.getFit(child, item) || child;

        // TODO: How de we handle childs with more than one connection point here?
        const childPoint = getOnlyMatchingConnectionPoint(fitted, item);

        if (childPoint) {
          return fitToPoint(fitted, childPoint, boundsType);
        }

        return { x: 0, y: 0, z: 0, width: 0, height: 0, depth: 0 };
      })
    );
    pos = { x: merged.x, y: merged.y, z: merged.z };
    size = { width: merged.width, height: merged.height, depth: merged.depth };
  } else {
    pos = bounds.pos;
    size = bounds.size;
  }

  const out = Object.assign({}, item, size, point);
  if (point.ry) {
    // assume == PI
    out.x -= size.width;
    out.x -= pos.x;
  } else {
    out.x += pos.x;
  }
  out.y += pos.y;
  out.z += pos.z;

  const offsets = getOffsets(item, point.name);
  out.x -= offsets.x;
  out.y -= offsets.y;
  out.z -= offsets.z;

  return out;
}

export function expandBounds(item, bounds) {
  const collisionBounds = item.id
    ? getBounds(getModel(item), 'collision')
    : { pos: item };
  function expand(axis, prop) {
    const replace = item[prop];
    if (Number.isFinite(replace)) {
      const offset =
        axis && bounds.pos
          ? bounds.pos[axis] || 0 - collisionBounds.pos[axis] || 0
          : 0;
      return Math.max(replace, bounds.size[prop]) - 2 * offset;
    }
    return bounds.size[prop];
  }
  return {
    ...bounds,
    size: [
      ['x', 'width'],
      [false, 'height'],
      [false, 'depth'],
    ].reduce(
      (out, [axis, prop]) => {
        out[prop] = expand(axis, prop);
        return out;
      },
      { ...bounds.size }
    ),
  };
}

export function getBoundedItem(item, boundsType) {
  let bounds = getBounds(getModel(item), boundsType);

  if (productService.isExtendable(item)) {
    bounds = expandBounds(item, bounds);
  }

  return Object.assign({}, item, bounds.size, {
    boundsType: bounds.type,
    x: item.x + bounds.pos.x,
    y: item.y + bounds.pos.y,
    z: item.z + bounds.pos.z,
  });
}

/**
 * Checks whether two item intersect each other based on their model bounds
 *
 * @param {Object} present The already present item (in the TAC)
 * @param {Object} placed The "incoming" item
 * @param {Object} placedFitted Collision/space versions of placed, fitted to a point
 * @returns {Boolean}
 */
function intersects(present, placed, placedFitted) {
  if (placed.itemid === present.itemid) {
    return false;
  }

  const presentCollisionItem = getBoundedItem(present, 'collision');
  const placedCollisionItem = placedFitted.fittedCollisionItem;

  // easy case, use collision bounds for both
  if (geometry.collides(presentCollisionItem, placedCollisionItem)) {
    return true;
  }

  const ignorePlacedSpace = tacHelpers.shouldIgnoreSpacingBounds(
    placedCollisionItem,
    present
  );
  const ignorePresentSpace = tacHelpers.shouldIgnoreSpacingBounds(
    present,
    placedCollisionItem
  );

  if (ignorePlacedSpace && ignorePresentSpace) {
    return false;
  }

  const presentSpaceItem = getBoundedItem(present, 'space');
  const placedSpaceItem = placedFitted.fittedSpaceItem;

  if (ignorePlacedSpace) {
    return geometry.collides(placedCollisionItem, presentSpaceItem);
  } else if (ignorePresentSpace) {
    return geometry.collides(placedSpaceItem, presentCollisionItem);
  } else {
    return (
      geometry.collides(placedCollisionItem, presentSpaceItem) ||
      geometry.collides(placedSpaceItem, presentCollisionItem)
    );
  }
}

function connectionPointBlocked(point, item, parent, fittedItems, items = []) {
  const collisionItems = items.filter(
    otherItem =>
      !tacHelpers.shouldIgnoreCollision(
        fittedItems.fittedCollisionItem,
        parent,
        otherItem
      )
  );
  return collisionItems.some(present => intersects(present, item, fittedItems));
}

export function rebasedItems(items, parent = { x: 0, y: 0, z: 0 }, omit = {}) {
  return items
    .filter(item => item.itemid !== omit.itemid)
    .reduce((out, item) => {
      const rebasedItem = {
        ...item,
        x: item.x + parent.x,
        y: item.y + parent.y,
        z: item.z + parent.z,
      };
      out.push(rebasedItem);
      if (item.items) {
        out.push(...rebasedItems(item.items, rebasedItem, omit));
      }
      return out;
    }, []);
}

function hitsLimits(point, item, parent, tac, fittedItems) {
  // Legs are allowed to collide with floor
  if (productService.isType(item, 'leg') || tacHelpers.isPartOf(item, parent)) {
    return false;
  }

  const floor = {
    x: -80000,
    y: -1,
    z: -1000,
    height: 1,
    width: 160000,
    depth: 2000,
  };
  const items = [
    fittedItems.fittedSpaceItem,
    fittedItems.fittedCollisionItem,
  ].map(item => ({
    ...item,
    x: parent.x + item.x,
    y: parent.y + item.y,
    z: parent.z + item.z,
  }));

  const ceiling = { ...floor, y: 160000 };
  if (tac.wall && tac.wall.points) {
    const room = geometry.surround(tac.wall.points);
    ceiling.y = room.height - room.y;
  }

  return items.some(item => {
    return (
      !productService.shouldIgnoreFloorAndCeilingCollision(item) &&
      (geometry.collides(item, floor) || geometry.collides(item, ceiling))
    );
  });
}

export function connectionPointOpen(point, item, parent, allItems, tac) {
  const fittedSpaceItem = fitToPoint(item, point, 'space');
  const fittedCollisionItem = fitToPoint(item, point, 'collision');
  const fittedItems = {
    fittedSpaceItem: { ...fittedSpaceItem, boundsType: 'space' },
    fittedCollisionItem: { ...fittedCollisionItem, boundsType: 'collision' },
  };
  const rebasedParent = allItems.find(item => item.itemid === parent.itemid);
  const zeroParent = Object.assign({}, parent, { x: 0, y: 0, z: 0 });

  const blocksInside = () =>
    connectionPointBlocked(point, item, parent, fittedItems, parent.items);

  const blocksOutside = () =>
    connectionPointBlocked(
      point,
      item,
      zeroParent,
      fittedItems,
      allItems
        .filter(
          item =>
            item.itemid !== parent.itemid &&
            !geometry.contains(item, rebasedParent)
        )
        // since the item we're fitting has coords relative to the containing section,
        // stuff outside needs rebasing to the same,
        // hence the subtraction of section's coords.
        .map(item =>
          Object.assign({}, item, {
            x: item.x - rebasedParent.x,
            y: item.y - rebasedParent.y,
            z: item.z - rebasedParent.z,
          })
        )
    ) || hitsLimits(point, item, rebasedParent, tac, fittedItems);

  if (
    productService.isType(item, [
      ITEMS.CLOTHES_RAIL,
      'side-panel',
      'post',
      'cross-brace',
    ])
  ) {
    /*
    Clothes rails cannot be mounted on both sides of a connection point,
    side panels cannot collide with other side panels
    */
    return !blocksInside() && !blocksOutside();
  }

  if (geometry.contains(zeroParent, fittedSpaceItem)) {
    return !blocksInside();
  }

  if (!geometry.collides(zeroParent, fittedSpaceItem)) {
    return !blocksOutside();
  }

  return !blocksInside() && !blocksOutside();
}

export function getOpenMatchingConnections(item, tac, proposedParent = null) {
  const connectionGroups = proposedParent
    ? getMatchingConnections(item, { ...tac, items: [proposedParent] })
    : getMatchingConnections(item, tac);
  const allItems = rebasedItems(tac.items, undefined, item);
  connectionGroups.forEach(parent => {
    parent.connections = parent.connections.filter(point =>
      connectionPointOpen(point, item, parent, allItems, tac)
    );
  });
  return connectionGroups;
}

export function isStandAlone(item) {
  const model = getModel(item);
  return isFloorStanding(model) || isWallMounted(model);
}

export function topPropBounds(item) {
  const nothing = {
    height: 0,
    width: 0,
    depth: 0,
  };
  if (item.propping) {
    const stripped = getBounds(item, 'size');
    const bounds = getProppingBounds(item);
    const used = bounds.find(bounds => 1 * bounds.id === item.propping);
    if (!used) {
      return nothing;
    }
    const diff = {
      height:
        used.pos.y + used.size.height - stripped.size.height - stripped.pos.y,
      width:
        used.pos.x + used.size.width - stripped.size.width - stripped.pos.y,
      depth:
        used.pos.z + used.size.depth - stripped.size.depth - stripped.pos.z,
    };
    if (diff.height > 0) {
      return diff;
    }
  }

  return nothing;
}
